"use strict";
/**
 * The composition contract — the exact set of choices the engine makes for one design.
 *
 * COMPOSITION IS DETERMINISTIC. It used to be an Azure OpenAI call, and that made sense while the model
 * chose the archetype by reading a free-text style prompt. v2 removed both inputs: the user now picks
 * the layout, and the style field was replaced by two structured selects whose behaviour is defined by
 * a table. With nothing left to interpret, a model call would be a random roll with an API bill and a
 * second of latency attached — so the roll is done locally, from a seed, and generation became instant
 * and free.
 *
 * The AI still has jobs, just not this one: interpreting a refinement request, and writing the prompts
 * for generated imagery. Both of those have actual free text to reason about.
 *
 * This is the line between generating and reskinning, and it is drawn deliberately.
 *
 * The archetype fixes what makes a layout WORK: reading order, which roles exist, and the structural
 * response to canvas shape (a headline beside a photo on a wide canvas, above it on a tall one). The
 * model decides what makes a design LOOK like something: the background treatment, which brand colour
 * carries which role, the type ramp, the decorative density, and — bounded — the proportions.
 *
 * Every field is an enum or a quantised number. That is not timidity: Azure's strict structured output
 * can only guarantee a response conforms if the schema is closed, and a free-form number invites the
 * model to emit 847.3184 for a coordinate it has no way to reason about. Bounded proportions give the
 * silhouette real movement without handing over geometry the model cannot check.
 *
 * The one thing a reviewer should test: with these slots, do two different style prompts produce
 * visibly different output? If they do not, the fix belongs here, in the slot inventory — not in the
 * prompt.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.COLOR_MODE_OPTIONS = exports.COLOR_MODES = exports.COLOR_EMPHASIS_OPTIONS = exports.COLOR_EMPHASES = exports.CORNER_RADIUS_AT_1080 = exports.CORNER_STYLES = exports.SCRIM_OPACITIES = exports.PROPORTION_STEPS = exports.ACCENT_DENSITIES = exports.WEIGHT_PAIR = exports.WEIGHT_CONTRASTS = exports.TYPE_RAMP_SUBHEAD_FACTOR = exports.TYPE_RAMPS = exports.GRADIENT_SPREADS = exports.GRADIENT_ANGLE = exports.GRADIENT_DIRECTIONS = exports.BACKGROUND_FAMILIES = exports.PALETTE_SLOTS = exports.ARCHETYPES_NEEDING_IMAGE = exports.ARCHETYPE_CATALOG = exports.ARCHETYPE_IDS = void 0;
exports.ARCHETYPE_IDS = [
    'gradient-text-only',
    'centered-statement',
    'photo-scrim-panel',
    'split-horizontal',
    'photo-band',
];
/**
 * What each archetype is CALLED, and when to reach for it.
 *
 * Data rather than strings in a component, because these are product copy: the internal ids
 * (`photo-scrim-panel`) leaked into the picker in v1, and now that choosing a layout is mandatory and
 * user-facing the names will be revised by whoever owns the words, without touching the component.
 */
exports.ARCHETYPE_CATALOG = [
    {
        id: 'photo-scrim-panel',
        name: 'Photo with text panel',
        hint: 'Text sits on a solid panel over the photo — safest with busy images',
        needsImage: true,
    },
    {
        id: 'split-horizontal',
        name: 'Split',
        hint: 'Photo and text in separate halves, cleanly divided',
        needsImage: true,
    },
    {
        id: 'photo-band',
        name: 'Text with photo band',
        hint: 'Room for longer copy above and below a supporting image',
        needsImage: true,
    },
    {
        id: 'gradient-text-only',
        name: 'Statement',
        hint: 'No photo — large text on a brand gradient',
        needsImage: false,
    },
    {
        id: 'centered-statement',
        name: 'Centred statement',
        hint: 'Short, symmetrical, formal — good for announcements',
        needsImage: false,
    },
];
/** Archetypes with an image region. The rest ignore a chosen image entirely. */
exports.ARCHETYPES_NEEDING_IMAGE = [
    'photo-scrim-panel',
    'split-horizontal',
    'photo-band',
];
/** Which slot of the workspace palette a role draws from. Resolved against real hexes server-side. */
exports.PALETTE_SLOTS = ['primary', 'secondary', 'accent', 'neutral', 'ink', 'paper'];
exports.BACKGROUND_FAMILIES = ['solid', 'linear-gradient', 'radial-gradient'];
/**
 * Gradient direction, quantised to eight compass points.
 *
 * Eight rather than free degrees because the visible difference between 137° and 142° is nil, while the
 * difference between 135° and 180° is the whole mood of the piece. Quantising also makes the "two
 * prompts differ" assertion meaningful — a change of direction is a change you can see.
 */
exports.GRADIENT_DIRECTIONS = [
    'to-bottom',
    'to-top',
    'to-right',
    'to-left',
    'to-bottom-right',
    'to-bottom-left',
    'to-top-right',
    'to-top-left',
];
exports.GRADIENT_ANGLE = {
    'to-bottom': 180,
    'to-top': 0,
    'to-right': 90,
    'to-left': 270,
    'to-bottom-right': 135,
    'to-bottom-left': 225,
    'to-top-right': 45,
    'to-top-left': 315,
};
/** How far the gradient's stops sit from the ends — a soft wash versus a hard band. */
exports.GRADIENT_SPREADS = ['full', 'soft', 'tight'];
/**
 * Type ramp: how far apart the headline and the subhead sit.
 *
 * A MULTIPLIER on the archetype's own authored subhead size, not a replacement for it. The five
 * reference renders use headline:subhead ratios of 2.42, 2.47, 2.07, 2.27 and 1.93 — each chosen for
 * that layout — so imposing one global ratio would overwrite five deliberate design decisions with an
 * average. `balanced` is the authored design exactly; the other two tighten or widen the gap.
 */
exports.TYPE_RAMPS = ['compact', 'balanced', 'dramatic'];
exports.TYPE_RAMP_SUBHEAD_FACTOR = {
    /** Subhead closer to the headline — denser, more editorial. */
    compact: 1.12,
    /** The archetype as authored. */
    balanced: 1,
    /** Subhead pulled down — more hierarchy, more air. */
    dramatic: 0.88,
};
/** Weight contrast between headline and body. */
exports.WEIGHT_CONTRASTS = ['subtle', 'normal', 'strong'];
exports.WEIGHT_PAIR = {
    subtle: { headline: 600, body: 400 },
    normal: { headline: 700, body: 400 },
    strong: { headline: 800, body: 300 },
};
/** Decorative density. `none` is a real choice — a clinical brand wants no rule at all. */
exports.ACCENT_DENSITIES = ['none', 'rule', 'rule-wide', 'bar'];
/**
 * The bounded proportional levers.
 *
 * These are what let the silhouette actually move. Each is a fraction of the canvas, quantised to
 * steps a viewer can see, and clamped by the archetype to a range that cannot break the layout — a
 * split at 0.35 or 0.65 is a different design; a split at 0.02 is a mistake.
 */
exports.PROPORTION_STEPS = [0.35, 0.4, 0.45, 0.5, 0.55, 0.6, 0.65];
/** Panel/scrim opacity, quantised. Below ~0.7 text legibility over a photo collapses. */
exports.SCRIM_OPACITIES = [0.7, 0.8, 0.9, 0.95, 1];
exports.CORNER_STYLES = ['square', 'soft', 'round'];
exports.CORNER_RADIUS_AT_1080 = {
    square: 0,
    soft: 16,
    round: 40,
};
/**
 * How the palette is distributed across the design.
 *
 * `keep` means genuinely untouched — a custom template keeps its own colours exactly, and an archetype
 * keeps its authored assignment. Someone who spent an afternoon on a template gets it respected.
 */
exports.COLOR_EMPHASES = ['keep', 'brand-forward', 'neutral-accents', 'high-contrast'];
exports.COLOR_EMPHASIS_OPTIONS = [
    { value: 'keep', label: 'Keep as is', hint: 'Leave the template’s own colours untouched.' },
    { value: 'brand-forward', label: 'Brand-forward', hint: 'Primary colour dominates; accent carries rules and highlights.' },
    { value: 'neutral-accents', label: 'Neutral with accents', hint: 'Mostly neutral ground, brand colours used sparingly.' },
    { value: 'high-contrast', label: 'High contrast', hint: 'Maximum separation between ground and text; heavier weight.' },
];
/**
 * Ground luminance. Governs SURFACES, never the photograph — `dark` darkens a scrim and puts light text
 * on it; it does not tint, desaturate or otherwise filter an image the user chose.
 */
exports.COLOR_MODES = ['keep', 'dark', 'light'];
exports.COLOR_MODE_OPTIONS = [
    { value: 'keep', label: 'Keep as is', hint: 'Whatever the template or archetype specifies.' },
    { value: 'dark', label: 'Dark', hint: 'Dark ground, light text; scrims and panels darkened.' },
    { value: 'light', label: 'Light', hint: 'Light ground, dark text; scrims and panels lightened.' },
];
//# sourceMappingURL=design-composition.js.map